import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faCartShopping, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import Cookie from "cookie-universal";
import { Axios } from "../../../API/Axios";
import { Pro } from "../../../API/Api";
import { useCart } from "../../../Context/CartChangerContext";
import { useLanguage } from "../../../Context/LanguageContext";
import "./Checkout.css";

const cookieJar = Cookie();

function priceAfterDiscount ( price, discount )
{
    const p = Number( price ) || 0;
    const d = Number( discount ) || 0;
    return d > 0 ? p - ( p * d ) / 100 : p;
}

export default function Checkout ()
{
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [ searchParams ] = useSearchParams();
    const directProductId = searchParams.get( "product_id" );

    const { cart, loading: cartLoading, setMenuischange } = useCart();

    const [ directProduct, setDirectProduct ] = useState( null );
    const [ directLoading, setDirectLoading ] = useState( !!directProductId );
    const [ count, setCount ] = useState( Number( searchParams.get( "count" ) ) || 1 );

    const [ shipping, setShipping ] = useState( { phone: "", city: "", address: "", notes: "" } );
    const [ paymentMethod, setPaymentMethod ] = useState( "cod" );

    const [ submitting, setSubmitting ] = useState( false );
    const isSubmittingRef = useRef( false );
    const [ pendingPaymentOrder, setPendingPaymentOrder ] = useState( null ); // الطلب بعد إنشائه، وقبل تأكيد الدفع الإلكتروني
    const [ error, setError ] = useState( "" );

    const isDirectMode = !!directProductId;
    const token = cookieJar.get( "ecommerce" );

    useEffect( () =>
    {
        if ( !token )
        {
            navigate( "/auth", { state: { from: `/checkout${ isDirectMode ? `?product_id=${ directProductId }` : "" }` } } );
            return;
        }

        if ( isDirectMode )
        {
            Axios.get( `/${ Pro }/${ directProductId }` )
                .then( ( res ) =>
                {
                    // GET /product/{id} بيرجّع مصفوفة فيها عنصر واحد [{...}]، مش كائن مباشر
                    const product = Array.isArray( res.data ) ? res.data[ 0 ] : res.data;
                    setDirectProduct( product || null );
                } )
                .catch( () => setError( "تعذر تحميل بيانات المنتج" ) )
                .finally( () => setDirectLoading( false ) );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    async function handleConfirm ()
    {
        // قفل فوري (ref) بدل الاعتماد على state بس - يمنع الضغط المتكرر السريع
        // حتى لو الضغطتين حصلوا قبل ما الواجهة تتحدث
        if ( isSubmittingRef.current ) return;
        isSubmittingRef.current = true;

        if ( !shipping.phone.trim() || !shipping.city.trim() || !shipping.address.trim() )
        {
            setError( t( "checkout.missingAddress" ) );
            isSubmittingRef.current = false;
            return;
        }

        setSubmitting( true );
        setError( "" );
        try
        {
            const body = {
                ...shipping,
                payment_method: paymentMethod,
                ...( isDirectMode ? { product_id: Number( directProductId ), count } : {} ),
            };

            const res = await Axios.post( "/orders", body );

            // لو الطلب من السلة، السلة بتتفضّى في الباك-إند، فنحدّث حالتها هنا كمان
            if ( !isDirectMode )
            {
                setMenuischange( ( prev ) => !prev );
            }

            if ( paymentMethod === "online" )
            {
                // الطلب اتسجل بحالة "بانتظار الدفع" - دلوقتي نعرض فورم Moyasar
                // بدل ما نروح على صفحة الطلبات فورًا
                setPendingPaymentOrder( res.data );
            } else
            {
                navigate( "/orders", { state: { justOrderedId: res.data.id } } );
            }
        } catch ( err )
        {
            if ( err.response?.status === 422 )
            {
                setError( err.response.data.message || "تعذر إتمام الطلب" );
            } else if ( err.response?.status === 401 )
            {
                navigate( "/auth" );
            } else
            {
                setError( t( "checkout.genericError" ) );
            }
        } finally
        {
            setSubmitting( false );
            isSubmittingRef.current = false;
        }
    }

    // بمجرد ما الطلب يتسجل بنجاح لدفع إلكتروني، نهيّئ فورم Moyasar
    useEffect( () =>
    {
        if ( !pendingPaymentOrder || !window.Moyasar ) return;

        window.Moyasar.init( {
            element: '.mysr-form',
            // المبلغ لازم يكون بالهللة (أصغر وحدة عملة) - نضرب في 100
            amount: Math.round( Number( pendingPaymentOrder.total ) * 100 ),
            currency: 'SAR',
            description: `Order #${ pendingPaymentOrder.id }`,
            publishable_api_key: process.env.REACT_APP_MOYASAR_PUBLISHABLE_KEY,
            callback_url: `${ window.location.origin }/payment/verify?order_id=${ pendingPaymentOrder.id }`,
            supported_networks: [ 'visa', 'mastercard', 'mada' ],
            methods: [ 'creditcard' ],
        } );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ pendingPaymentOrder ] );

    // ------------------ حالة التحميل ------------------
    if ( ( isDirectMode && directLoading ) || ( !isDirectMode && cartLoading ) )
    {
        return <div className="checkout-page" />;
    }

    // ------------------ فورم الدفع الإلكتروني (بعد إنشاء الطلب بحالة "بانتظار الدفع") ------------------
    if ( pendingPaymentOrder )
    {
        return (
            <div className="checkout-page">
                <div className="checkout-container">
                    <h1 className="checkout-title">
                        <FontAwesomeIcon icon={ faBoxOpen } />
                        { t( "checkout.pageTitle" ) }
                    </h1>
                    <div className="checkout-panel">
                        <p className="checkout-payment-order-note">
                            { t( "checkout.orderCreatedAwaitingPayment", { id: pendingPaymentOrder.id } ) }
                        </p>
                        <div className="mysr-form"></div>
                    </div>
                </div>
            </div>
        );
    }

    // ------------------ وضع "اطلب الآن" ------------------
    if ( isDirectMode )
    {
        if ( !directProduct )
        {
            return (
                <div className="checkout-page">
                    <div className="checkout-empty">
                        <p>{ error || t( "checkout.productUnavailable" ) }</p>
                        <Link to="/shop" className="checkout-secondary-link">{ t( "checkout.browseShop" ) }</Link>
                    </div>
                </div>
            );
        }

        const img = directProduct.images?.[ 0 ]?.image;
        const unitPrice = priceAfterDiscount( directProduct.price, directProduct.discount );
        const total = unitPrice * count;
        const maxStock = directProduct.stock ?? 99;

        return (
            <div className="checkout-page">
                <div className="checkout-container">
                    <h1 className="checkout-title">
                        <FontAwesomeIcon icon={ faBoxOpen } />
                        { t( "checkout.pageTitle" ) }
                    </h1>

                    <div className="checkout-panel">
                        <div className="checkout-row">
                            { img ? (
                                <img src={ img } alt={ directProduct.title } className="checkout-item-img" />
                            ) : (
                                <div className="checkout-item-img checkout-item-img--placeholder" />
                            ) }
                            <div className="checkout-item-info">
                                <p className="checkout-item-title">{ directProduct.title }</p>
                                <p className="checkout-item-price">{ unitPrice.toFixed( 2 ) } ر.س</p>
                            </div>

                            <div className="checkout-qty">
                                <button
                                    type="button"
                                    onClick={ () => setCount( ( c ) => Math.max( 1, c - 1 ) ) }
                                    disabled={ count <= 1 }
                                >
                                    <FontAwesomeIcon icon={ faMinus } />
                                </button>
                                <span>{ count }</span>
                                <button
                                    type="button"
                                    onClick={ () => setCount( ( c ) => Math.min( maxStock, c + 1 ) ) }
                                    disabled={ count >= maxStock }
                                >
                                    <FontAwesomeIcon icon={ faPlus } />
                                </button>
                            </div>
                        </div>

                        <div className="checkout-shipping-form">
                            <h2 className="checkout-section-title">{ t( "checkout.shippingTitle" ) }</h2>
                            <input
                                type="tel"
                                placeholder={ t( "checkout.phonePlaceholder" ) }
                                value={ shipping.phone }
                                onChange={ ( e ) => setShipping( { ...shipping, phone: e.target.value } ) }
                            />
                            <input
                                type="text"
                                placeholder={ t( "checkout.cityPlaceholder" ) }
                                value={ shipping.city }
                                onChange={ ( e ) => setShipping( { ...shipping, city: e.target.value } ) }
                            />
                            <textarea
                                placeholder={ t( "checkout.addressPlaceholder" ) }
                                value={ shipping.address }
                                onChange={ ( e ) => setShipping( { ...shipping, address: e.target.value } ) }
                                rows={ 2 }
                            />
                            <textarea
                                placeholder={ t( "checkout.notesPlaceholder" ) }
                                value={ shipping.notes }
                                onChange={ ( e ) => setShipping( { ...shipping, notes: e.target.value } ) }
                                rows={ 2 }
                            />

                            <div className="checkout-payment-methods">
                                <button
                                    type="button"
                                    className={ `checkout-payment-btn ${ paymentMethod === "cod" ? "is-active" : "" }` }
                                    onClick={ () => setPaymentMethod( "cod" ) }
                                >
                                    { t( "checkout.payCod" ) }
                                </button>
                                <button
                                    type="button"
                                    className={ `checkout-payment-btn ${ paymentMethod === "online" ? "is-active" : "" }` }
                                    onClick={ () => setPaymentMethod( "online" ) }
                                >
                                    { t( "checkout.payOnline" ) }
                                </button>
                            </div>
                        </div>

                        <div className="checkout-total">
                            <span>{ t( "checkout.total" ) }</span>
                            <strong>{ total.toFixed( 2 ) } ر.س</strong>
                        </div>

                        { error && <p className="checkout-error">{ error }</p> }

                        <button
                            className="checkout-confirm-btn"
                            onClick={ handleConfirm }
                            disabled={ submitting }
                        >
                            { submitting ? t( "checkout.confirming" ) : t( "checkout.confirmBtn" ) }
                        </button>
                        <Link to={ `/product/${ directProductId }` } className="checkout-secondary-link">
                            { t( "checkout.backToProduct" ) }
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ------------------ وضع "إتمام الشراء من السلة" ------------------
    if ( !cart || cart.length === 0 )
    {
        return (
            <div className="checkout-page">
                <div className="checkout-empty">
                    <FontAwesomeIcon icon={ faCartShopping } />
                    <p>{ t( "checkout.emptyCart" ) }</p>
                    <Link to="/shop" className="checkout-secondary-link">{ t( "checkout.browseShop" ) }</Link>
                </div>
            </div>
        );
    }

    const cartTotal = cart.reduce( ( sum, item ) =>
    {
        const unitPrice = priceAfterDiscount( item.product?.price, item.product?.discount );
        return sum + unitPrice * item.count;
    }, 0 );

    return (
        <div className="checkout-page">
            <div className="checkout-container">
                <h1 className="checkout-title">
                    <FontAwesomeIcon icon={ faBoxOpen } />
                    { t( "checkout.pageTitle" ) }
                </h1>

                <div className="checkout-panel">
                    <div className="checkout-list">
                        { cart.map( ( item ) =>
                        {
                            const product = item.product || {};
                            const img = product.images?.[ 0 ]?.image;
                            const unitPrice = priceAfterDiscount( product.price, product.discount );
                            return (
                                <div className="checkout-row" key={ item.id }>
                                    { img ? (
                                        <img src={ img } alt={ product.title } className="checkout-item-img" />
                                    ) : (
                                        <div className="checkout-item-img checkout-item-img--placeholder" />
                                    ) }
                                    <div className="checkout-item-info">
                                        <p className="checkout-item-title">{ product.title }</p>
                                        <p className="checkout-item-price">
                                            { item.count } × { unitPrice.toFixed( 2 ) } ر.س
                                        </p>
                                    </div>
                                </div>
                            );
                        } ) }
                    </div>

                    <p className="checkout-edit-note">
                        { t( "checkout.editCartNote" ) }{ " " }
                        <Link to="/profile">{ t( "checkout.backToCart" ) }</Link>
                    </p>

                    <div className="checkout-shipping-form">
                        <h2 className="checkout-section-title">{ t( "checkout.shippingTitle" ) }</h2>
                        <input
                            type="tel"
                            placeholder={ t( "checkout.phonePlaceholder" ) }
                            value={ shipping.phone }
                            onChange={ ( e ) => setShipping( { ...shipping, phone: e.target.value } ) }
                        />
                        <input
                            type="text"
                            placeholder={ t( "checkout.cityPlaceholder" ) }
                            value={ shipping.city }
                            onChange={ ( e ) => setShipping( { ...shipping, city: e.target.value } ) }
                        />
                        <textarea
                            placeholder={ t( "checkout.addressPlaceholder" ) }
                            value={ shipping.address }
                            onChange={ ( e ) => setShipping( { ...shipping, address: e.target.value } ) }
                            rows={ 2 }
                        />
                        <textarea
                            placeholder={ t( "checkout.notesPlaceholder" ) }
                            value={ shipping.notes }
                            onChange={ ( e ) => setShipping( { ...shipping, notes: e.target.value } ) }
                            rows={ 2 }
                        />

                        <div className="checkout-payment-methods">
                            <button
                                type="button"
                                className={ `checkout-payment-btn ${ paymentMethod === "cod" ? "is-active" : "" }` }
                                onClick={ () => setPaymentMethod( "cod" ) }
                            >
                                { t( "checkout.payCod" ) }
                            </button>
                            <button
                                type="button"
                                className={ `checkout-payment-btn ${ paymentMethod === "online" ? "is-active" : "" }` }
                                onClick={ () => setPaymentMethod( "online" ) }
                            >
                                { t( "checkout.payOnline" ) }
                            </button>
                        </div>
                    </div>

                    <div className="checkout-total">
                        <span>{ t( "checkout.total" ) }</span>
                        <strong>{ cartTotal.toFixed( 2 ) } ر.س</strong>
                    </div>

                    { error && <p className="checkout-error">{ error }</p> }

                    <button
                        className="checkout-confirm-btn"
                        onClick={ handleConfirm }
                        disabled={ submitting }
                    >
                        { submitting ? t( "checkout.confirming" ) : t( "checkout.confirmBtn" ) }
                    </button>
                </div>
            </div>
        </div>
    );
}
